import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import handleHospital from "@/services/modules/hospital";
import htmlToPdfmake from 'html-to-pdfmake';
import { addCommas, convertSuperscriptToHtml, convertSuperscriptToPdfMake, formatDate, formatToDdMmYy } from './global';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

const pdfPrinter = () => {

    const { dateOfBarthConvertToAge } = handleHospital();

    const exportToPDF = async (company, investigation, barcode, qrcode,
                               barcodePatient, userName, btnTitle, isHideHeader = false,
                               isHideFooter = false, isDownload = false, customizationFields = []) => {

        const isBorder = customizationFields['heading_left_details_border'];

        let margins = null;

        if(customizationFields['extra_settings_margin'] && customizationFields['extra_settings_margin'].is_active) {
            margins = customizationFields['extra_settings_margin'].value
        }
        
        let canvasHeight = 0;

        const properties = getFooterProperties(investigation, customizationFields);

        canvasHeight = properties.topMargin;

        var doc = {
            pageSize: 'A4',
            pageMargins: getPageMargin(margins),
            header: getHeader(company, customizationFields, margins, isHideHeader),
            footer: (currentPage, pageCount) => {
                const regularFooter = getFooter(userName, qrcode, customizationFields, margins);
                
                if (currentPage === pageCount) {
                    return getFooterTop(regularFooter, properties, isHideFooter);
                }
                
                return !isHideFooter ? regularFooter : '';
            },
            content: getContent(investigation, barcode, barcodePatient, userName, btnTitle, isBorder, customizationFields, margins),

            styles : {
                header: {
                    fontSize: 16,
                },
                tableHeader: {
                    fontSize: 16,
                },
                tableExample: {
                    fontSize: 10,
                },
            },

            defaultStyle: {
                color: 'black',
                fontSize: 10,
                font: 'SulaimanLipi'
            },

            info: {
                title: investigation.id
            }
        }

        pushIntoTable(
            doc,
            investigation.formattedGenerals,
            investigation.antibiotics_dropdown,
            canvasHeight
        );

        const pdfDocGenerator = pdfMake.createPdf(doc);
        if(isDownload) {
            pdfDocGenerator.download('investigation-report.pdf');
            return;
        }
        pdfDocGenerator.print();
    }

    const getFooterProperties = (investigation, customizationFields) => {
        
        const preparedByResource = investigation.prepared_by_resource || {};
        const approvedByResource = investigation.approved_by_resource || {};

        const preparedTexts = preparedByResource.short_description ? preparedByResource.short_description.split('\n') : [];
        const approvedTexts = approvedByResource.short_description ? approvedByResource.short_description.split('\n') : [];
        const hasPreparedSignatureImg = shouldInclude(customizationFields, 'signature_prepared_by_signature');
        const hasApprovedSignatureImg = shouldInclude(customizationFields, 'signature_approved_by_signature');
        const preparedSignatureImg = hasPreparedSignatureImg && preparedByResource.signature_photo64;
        const approvedSignatureImg = hasApprovedSignatureImg && approvedByResource.signature_photo64;
        let preparedTextAndImgCount = preparedTexts.length;
        let approvedTextAndImgCount = approvedTexts.length;
        let substraction =  0;

        if (preparedSignatureImg) {
            preparedTextAndImgCount += 4;
            substraction = 2;
        }

        if (approvedSignatureImg) {
            approvedTextAndImgCount += 4;
            substraction = 2;
        }

        let maxCount = Math.max(preparedTextAndImgCount, approvedTextAndImgCount);

        if (preparedTextAndImgCount === approvedTextAndImgCount) {
            maxCount += 1;
            substraction += 9;
        }

        let topMargin = generatePattern(16, maxCount) - substraction;

        return {
            topMargin,
            preparedByResource,
            approvedByResource,
            hasPreparedSignatureImg,
            hasApprovedSignatureImg,
            preparedTexts,
            approvedTexts,
            preparedSignatureImg,
            approvedSignatureImg
        }
    }

    const getFooterTop = (regularFooter, properties, isHideFooter) => {

        if (! properties.preparedTexts.length && ! properties.approvedTexts.length) {
            return regularFooter;
        }

        return {
            stack: [
                {
                    margin: [60, -(properties.topMargin), 60, 5],
                    columns: [
                        {
                            width: '40%',
                            alignment: 'left',
                            stack: [
                                (properties.preparedSignatureImg ? {
                                    image: properties.preparedSignatureImg,
                                    fit: [100, 40]
                                } : ''),
                                (properties.preparedTexts.length ? {
                                    stack: properties.preparedTexts,
                                    color: 'black',
                                    bold: true,
                                    fontSize: 9
                                }: ''),
                            ]
                        },
                        {width: '25%', text: ''},
                        {
                            width: '35%',
                            alignment: 'left',
                            // margin: [0, 0, -20, 0],
                            stack: [
                                (properties.approvedSignatureImg ? {
                                    image: properties.approvedSignatureImg,
                                    fit: [100, 40]
                                } : ''),
                                (properties.approvedTexts.length ? {
                                    stack: properties.approvedTexts,
                                    color: 'black',
                                    bold: true,
                                    fontSize: 9
                                }: ''),
                            ]
                        }
                    ]
                },
                !isHideFooter ? regularFooter : ''
            ]
        };
    }

    const generatePattern = (start, count) => {
        let current = start;

        for (let i = 0; i < count - 1; i++) {
            current += i % 2 === 0 ? 11 : 10;
        }

        return current;
    }

    const calculateAge = (birthday) => {
        const {year, month, day} = dateOfBarthConvertToAge(birthday)
        return `${year}Y, ${month}M, ${day}D`
    }

    const getHeader = (company, customizationFields, margins, isHideHeader) => {

        if (isHideHeader) return '';

        const showCoverPhoto = customizationFields['extra_settings_cover_photo_upload'] && customizationFields['extra_settings_cover_photo_upload'].is_active;

        // If cover photo is enabled, show only that and nothing else
        if (showCoverPhoto) {
            return {
                columns: [
                    {
                        image: customizationFields['extra_settings_cover_photo_upload'].value64,
                        fit: [600, (margins && margins.top) ? margins.top : 120],
                        alignment: 'center',
                    }
                ]
            };
        }

        // Otherwise, show normal header fields
        return {
            margin: margins ? [margins.left, 0, margins.right, 10] : [60, 0, 60, 10],
            columns: [
                shouldInclude(customizationFields, 'heading_left_logo') && company.logo64
                    ? {
                        alignment: 'left',
                        image: company.logo64,
                        maxHeight: 60
                    }
                    : null,
                {
                    alignment: 'right',
                    stack: [
                        shouldInclude(customizationFields, 'heading_right_company_name') && company.name ? company.name : '',
                        shouldInclude(customizationFields, 'heading_right_company_address') && company.address ? 'Address: ' + company.address : '',
                        shouldInclude(customizationFields, 'heading_right_company_phone') && company.phone ? 'Phone: ' + company.phone : '',
                        shouldInclude(customizationFields, 'heading_right_company_email') && company.email ? 'E-mail: ' + company.email : '',
                        shouldInclude(customizationFields, 'heading_right_vat_reg_number') && company.vat_no ? 'VAT Reg No: ' + company.vat_no : '',
                    ]
                }
            ].filter(Boolean)
        };
    };

    const getContent = (investigation, barcode, barcodePatient, userName, btnTitle, isBorder, customizationFields, margins) => {

        const reports = investigation.formattedGenerals;

        const hasAntibiotics = Object.values(reports).some(productGroups =>
            Object.values(productGroups).some(investigationArray =>
                investigationArray.some(item => item.investigation_report_type === 'antibiotics')
            )
        );

        // Prepare the left & right stacks
        const leftStack = [
            shouldInclude(customizationFields, 'body_top_left_patient_barcode') ? { svg: barcodePatient } : '',
            shouldInclude(customizationFields, 'body_top_left_patient_id') ?
                { columns: [{ width: '30%', text: 'Patient ID:', bold: true }, { width: '70%', text: investigation.contact_profile.serial_no }]} : '',
            shouldInclude(customizationFields, 'body_top_left_patient_name') ?
                { columns: [{ width: '30%', text: 'Patient Name:', bold: true }, { width: '70%', text: investigation.contact_profile.full_name.toUpperCase() }]} : '',
            shouldInclude(customizationFields, 'body_top_left_patient_age') ?
                { columns: [{ width: '30%', text: 'Age:', bold: true }, { width: '70%', text: calculateAge(investigation.contact_profile.birthday) + ' | Gender: ' + capitalize(investigation.contact_profile.gender) }]} : '',
            shouldInclude(customizationFields, 'body_top_left_patient_mobile_no') ?
                { columns: [{ width: '30%', text: 'Mobile No:', bold: true }, { width: '70%', text: investigation.contact_profile.mobile_no }]} : '',
            shouldInclude(customizationFields, 'body_top_left_ref_by') ?
                { columns: [{ width: '30%', text: 'Ref By:', bold: true }, { width: '70%', text: (investigation.service_resource && investigation.service_resource.consultant) ? investigation.service_resource.consultant.full_name : '' }]} : '',
            shouldInclude(customizationFields, 'body_top_left_specimen') ?
                { columns: [{ width: '30%', text: 'Specimen:', bold: true }, { width: '70%', text: getUniqueSamples(investigation.formattedGenerals) }]} : '',
        ].filter(Boolean);

        const rightStack = [
            shouldInclude(customizationFields, 'body_top_right_bill_barcode') ? { svg: barcode, alignment: 'right' } : '',
            shouldInclude(customizationFields, 'body_top_right_bill_no') ?
                { columns: [{ width: '50%', text: 'Bill NO:', bold: true }, { width: '50%', text: investigation.bill_number, alignment: 'right' }]} : '',
            shouldInclude(customizationFields, 'body_top_right_bill_date_time') ?
                { columns: [{ width: '50%', text: 'Bill Date and Time:', bold: true }, { width: '50%', text: formatToDdMmYy(investigation.date) + ' ' + investigation.created_time, alignment: 'right' }]} : '',
            shouldInclude(customizationFields, 'body_top_right_receive_date') ?
                { columns: [{ width: '50%', text: 'Received Date:', bold: true }, { width: '50%', text: formatToDdMmYy(investigation.date), alignment: 'right' }]} : '',
            shouldInclude(customizationFields, 'body_top_right_sample_collection_date') ?
                { columns: [{ width: '50%', text: 'Sample Collection:', bold: true }, { width: '50%', text: investigation.sample_collection_date, alignment: 'right' }]} : '',
            shouldInclude(customizationFields, 'body_top_right_report_date') ?
                { columns: [{ width: '50%', text: 'Report Date:', bold: true }, { width: '50%', text: investigation.report_date, alignment: 'right' }]} : '',
            shouldInclude(customizationFields, 'body_top_right_delivery_date') ?
                { columns: [{ width: '50%', text: 'Delivery Date:', bold: true }, { width: '50%', text: investigation.expected_delivery_format_date, alignment: 'right' }]} : '',
        ].filter(Boolean);

        return [
            {
                margin: [0, 5, 0, 0],
                stack: [

                    // Table content
                    {
                        table: {
                            widths: ['*'],
                            body: [
                                [
                                    {
                                        columns: [
                                            { width: '60%', stack: leftStack },
                                            { width: '40%', stack: rightStack }
                                        ]
                                    }
                                ]
                            ]
                        },
                        layout: isBorder ? {
                            hLineWidth: () => .5,
                            vLineWidth: () => .5,
                            hLineColor: () => '#82868b',
                            vLineColor: () => '#82868b',
                            paddingLeft: () => 8,
                            paddingRight: () => 8,
                            paddingTop: () => 6,
                            paddingBottom: () => 6
                        } : 'noBorders',
                    }
                ]
            },

            // Button section
            {
                margin: [0, 10, 0, 15],
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 'auto',
                        stack: [
                            {
                                canvas: [
                                    {
                                        type: 'rect',
                                        x: 0,
                                        y: 0,
                                        w: btnTitle.length * 8 + 30,
                                        h: 28,
                                        r: 6,
                                        lineWidth: 1,
                                        lineColor: '#000'
                                    }
                                ]
                            },
                            {
                                text: btnTitle.toUpperCase(),
                                bold: true,
                                fontSize: 14,
                                alignment: 'center',
                                margin: [0, -22, 0, 0],
                                color: '#000'
                            }
                        ]
                    },
                    { width: '*', text: '' }
                ]
            },

            ...(hasAntibiotics ? [{
                text: 'Culture & Sensitivity Report',
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 0, 5],
                fontSize:12,
                bold: true,
                color: 'black',
            }] : [])
        ]
    }

    const getFooter = (userName, qrcode, customizationFields, margins) => {
        const showCoverPhoto = customizationFields['extra_settings_footer_photo_upload'] && customizationFields['extra_settings_footer_photo_upload'].is_active;

        if (showCoverPhoto) {
            return {
                columns: [
                    {
                        image: customizationFields['extra_settings_footer_photo_upload'].value64,
                        fit: [600, (margins && margins.bottom) ? margins.bottom : 120],
                        alignment: 'center',
                    }
                ]
            };
        }

        return {
            margin: margins ? [margins.left, 0, margins.right, 10] : [60, 0, 60, 10],
            columns: [
                {
                    columns: [
                        shouldInclude(customizationFields,'footer_qrcode') ? {
                            svg: qrcode
                        } : '',

                        shouldInclude(customizationFields,'footer_note') ? {
                            width: '90%',
                            alignment: 'left',
                            text: 'N.B: This is a system generated documents and requires no manual signature.',
                            margin: [5, 53, 0, 0],
                            fontSize: 8
                        } : ''
                    ]
                },

                shouldInclude(customizationFields,'footer_printed_by') ? {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        {
                            text: 'Printed by',
                            bold: true
                        },
                        {
                            text: userName
                        },
                        {
                            text: formatDate(new Date())
                        }
                    ]
                } : ''
            ]
        }
    }

    const hasUnitColumn = (reports) =>
        reports.some(r => r.unit !== null && r.unit !== '');

    const hasReferenceColumn = (reports) =>
        reports.some(r => r.reference_value !== null && r.reference_value !== '');

    const pushIntoTable = (doc, mainReportData, antibioticsDropDown, canvasHeight) => {

        const mainStack = [];
        const machineImageData = [];
        let tableHeaderShow = true;

        Object.entries(mainReportData).forEach(([productId, reports], productIndex) => {
            const flattenedReportData = Object.values(reports).flat();
            const hasUnit = hasUnitColumn(flattenedReportData);
            const hasReference = hasReferenceColumn(flattenedReportData);
            Object.entries(reports).forEach(([head, group]) => {
                let i = 0;

                while (i < group.length) {
                    const item = group[i];

                    if (item.investigation_report_type === 'machine_image' && item.result) {
                        machineImageData.push(item);
                        i++;
                        continue;
                    }

                    if (item.investigation_report_type === 'general_table' && item.result) {
                        const tableItems = [];

                        while (i < group.length && group[i].investigation_report_type === 'general_table') {
                            tableItems.push(group[i]);
                            i++;
                        }

                        const tableHeader = getTableHeader(hasUnit, hasReference);

                        const content = {
                            style: 'tableExample',
                            margin: [0, 0, 0, 0],
                            table: {
                                widths: getTableWidths(hasUnit, hasReference),
                                body: []
                            },
                            layout: {
                                fillColor: function (rowIndex, node, columnIndex) {
                                    return '';
                                },
                                hLineWidth: function (i, node) {
                                    return 0.1;
                                },
                                vLineWidth: function (i, node) {
                                    return 0;
                                },
                                hLineColor: function (i, node) {
                                    return '#82868b';
                                },
                                vLineColor: function (i, node) {
                                    return (i === 0 || i === node.table.widths.length) ? '#82868b' : '#dddddd';
                                },
                                paddingTop: function (rowIndex) {
                                    return rowIndex === 0 ? 5 : 0;
                                },
                                paddingBottom: function (rowIndex) {
                                    return rowIndex === 0 ? 5 : 0;
                                }
                            }
                        };

                        if (tableHeaderShow) {
                            content.table.body.push(tableHeader);
                            tableHeaderShow = false
                        }
                        if (tableItems.length > 1) {
                            content.table.body.push(getRowTitle(head, hasUnit, hasReference));
                        }
                        setTableContent(tableItems, content, hasUnit, hasReference);
                        mainStack.push(content);
                    } else if (item.investigation_report_type === 'general_text' && item.result) {
                        mainStack.push({
                            text: '',
                            margin: [0, 5, 0, 5]
                        });
                        mainStack.push(getTextContent([item]));
                        mainStack.push({
                            text: '',
                            margin: [0, 5, 0, 5]
                        });
                        i++;
                    } else if (item.investigation_report_type === 'text_table' && item.result) {
                        const textTableItem = [];

                        while (i < group.length && group[i].investigation_report_type === 'text_table') {
                            textTableItem.push(group[i]);
                            i++;
                        }

                        mainStack.push({
                            text: '',
                            margin: [0, 5, 0, 5]
                        });
                        if (textTableItem.length > 1) {
                            mainStack.push({
                                text: head,
                                bold:true
                            });
                        }
                        mainStack.push(getTextTableContent(textTableItem));
                        mainStack.push({
                            text: '',
                            margin: [0, 5, 0, 5]
                        });
                        i++;
                    }
                    else if (item.investigation_report_type === 'antibiotics' && item.result) {
                        const antibioticsTableItem = [];

                        while (i < group.length && group[i].investigation_report_type === 'antibiotics') {
                            antibioticsTableItem.push(group[i]);
                            i++;
                        }

                        mainStack.push({
                            text: '',
                            margin: [0, 5, 0, 5]
                        });

                        mainStack.push(getAntibioticsTable(head, antibioticsTableItem, antibioticsDropDown));

                        mainStack.push({
                            table: {
                                widths: ['*', '*', '*'],
                                body: [
                                    [
                                        {
                                            text: 'R: Resistant',
                                            alignment: 'center',
                                            border: [false, false, false, false]
                                        },
                                        {
                                            text: 'M: Moderate',
                                            alignment: 'center',
                                            border: [false, false, false, false]
                                        },
                                        {
                                            text: 'S: Sensitive',
                                            alignment: 'center',
                                            border: [false, false, false, false]
                                        },
                                    ]
                                ]
                            },
                            layout: 'noBorders',
                            margin: [0, 5, 0, 5]
                        });
                        i++;
                    } else {
                        i++;
                    }
                }
            });

            const isLastProduct = productIndex === Object.keys(mainReportData).length - 1;

            if (!isLastProduct) {
                mainStack.push({
                    text: '',
                    margin: [0, 10, 0, 10]
                });
            }
        });

        const columns = [
            {
                width: '100%',
                stack: mainStack
            }
        ];

        // Add machine images in right column if they exist
        if (machineImageData.length > 0) {
            const imageColumn = {
                width: '20%',
                stack: getMachineImageColumn(machineImageData)
            };

            columns[0].width = '80%';
            columns.push(imageColumn);
        }

        doc.content.push({
            columns: columns,
        });

        if (canvasHeight > 0) {
            doc.content.push({
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 0,
                        h: canvasHeight,
                        color: 'white'
                    }
                ]
            });
        }
    };

    const getMachineImageColumn = (images = []) => {

        const stack = [];

        for (const img of images) {
            stack.push(
                {
                    image: img.result,
                    fit: [100, 100],
                    margin: [10, 0, 0, 5]
                },
                {
                    text: img.name || '',
                    fontSize: 9,
                    alignment: 'center',
                    margin: [5, 0, 0, 15],
                    italics: true
                }
            );
        }

        return stack;
    };

    const getTextContent = (items) => {
        if (!items || !items.length) return [];

        const content = [];

        items.forEach(item => {
            if (!item.result) return;
            let htmlString = convertSuperscriptToHtml(item.result);
            let pdfContent = htmlToPdfmake(htmlString);

            if (typeof pdfContent === 'string') {
                content.push({ text: pdfContent });
            }

            if (Array.isArray(pdfContent)) {
                pdfContent.forEach(c => {
                    content.push({ text: c });
                });
            }

            if (typeof pdfContent === 'object' && !Array.isArray(pdfContent)) {
                content.push({ ...pdfContent });
            }
        });

        return content;
    };

    const setTableContent = (reports, content, hasUnit, hasReference) => {
        for(const report of reports){
            const row = getRowData(report, reports, hasUnit, hasReference);
            if (row) {
                content.table.body.push(row);
            }
        }
    }

    const getTextTableContent = (textTable) => {

        const rows = textTable.map(item => {

            let htmlString = convertSuperscriptToHtml(item.result || '');
            let pdfContent = htmlToPdfmake(htmlString);

            return [
                {
                    text: item.name || '',
                },
                {
                    stack: Array.isArray(pdfContent)
                        ? pdfContent
                        : [pdfContent],
                }
            ];
        });

        return {
            style: 'tableExample',
            margin: [0, 5, 0, 5],
            table: {
                widths: ['*', '*'],
                body: [
                    [
                        { text: 'Test', bold: true },
                        { text: 'Result', bold: true },
                    ],
                    ...rows
                ]
            },
            layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
            }
        };
    };

    const getAntibioticsTable = (head, antibiotics, antibioticsDropDown) => {
        const half = Math.ceil(antibiotics.length / 2);
        const leftSide = antibiotics.slice(0, half);
        const rightSide = antibiotics.slice(half);

        const makeTableBody = (items, startIndex = 1) =>
            items.map((item, i) => [
                { text: (startIndex + i).toString(), alignment: 'center', margin: [0, 0, 3, 0] },
                { text: item.name || '', margin: [2, 0, 0, 0] },
                { text: antibioticsDropDown[item.result] || '', alignment: 'center', bold: true }
            ]);

        const leftBody = makeTableBody(leftSide, 1);
        const rightBody = makeTableBody(rightSide, half + 1);

        const maxRows = Math.max(leftBody.length, rightBody.length);
        const rows = [];

        for (let i = 0; i < maxRows; i++) {
            const left = leftBody[i] || [{ text: '' }, { text: '' }, { text: '' }];
            const right = rightBody[i] || [{ text: '' }, { text: '' }, { text: '' }];
            rows.push([...left, ...right]);
        }

        return {
            style: 'tableExample',
            margin: [0, 5, 0, 5],
            table: {
                widths: [15, '*', 50, 15, '*', 50],
                body: [
                    [
                        { text: 'SL', bold: true },
                        { text: 'Sensitivity Test', bold: true },
                        { text: 'Result', bold: true, alignment: 'center' },
                        { text: 'SL', bold: true },
                        { text: 'Sensitivity Test', bold: true },
                        { text: 'Result', bold: true, alignment: 'center' }
                    ],
                    ...rows
                ]
            },
            layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
            }
        };
    };

    const getTableHeader = (hasUnit, hasReference) => {
        const header = [
            { text: 'Test', bold: true, fontSize: 12},
            { text: 'Result', bold: true, fontSize: 12 }
        ];

        if (hasUnit) {
            header.push({ text: 'Unit', bold: true, alignment: 'left', fontSize: 12 });
        }

        if (hasReference) {
            header.push({ text: 'Reference value', bold: true, fontSize: 12 });
        }

        return header;
    };

    const getTableWidths = (hasUnit, hasReference) => {
        let widths = ['*', '18%'];

        if (!hasUnit && !hasReference){
            widths = ["50%", "*"]
        }

        if (hasUnit) widths.push('18%');
        if (hasReference) widths.push('35%');

        return widths;
    };

    const getRowTitle = (title, hasUnit, hasReference) => {
        let colSpan = 2;
        if (hasUnit) colSpan += 1;
        if (hasReference) colSpan += 1;

        const row = [
            { text: title, bold: true, colSpan: colSpan, margin: [0, 0, 0, 0] }
        ];

        for (let i = 1; i < colSpan; i++) {
            row.push({});
        }

        return row;
    };

    const getRowData = (report, reports, hasUnit, hasReference) => {
        if (!report.result) return null;

        const formattedResult = (
            report.result !== null &&
            report.result !== '' &&
            !isNaN(report.result) &&
            isFinite(report.result)
        )
            ? addCommas(report.result)
            : report.result;

        const row = [
            {
                text: report.name,
                bold: reports.length === 1,
                margin: reports.length === 1
                    ? [0, 0, 0, 0]
                    : [15, 0, 0, 0]
            },
            { text: formattedResult, bold: true }
        ];

        if (hasUnit) {
            row.push({ text: report.unit ? htmlToPdfmake(convertSuperscriptToHtml(report.unit)) : '' });
        }

        if (hasReference) {
            const lines = report.reference_value
                ? report.reference_value.split('\n')
                : [];

            row.push({
                stack: lines.map(line => ({
                    text: convertSuperscriptToPdfMake(line)
                }))
            });
        }

        return row;
    };

    const getUniqueSamples = (formattedGenerals) => {
        const samplesSet = new Set();

        Object.values(formattedGenerals).forEach(productGroups => {
            Object.values(productGroups).forEach(investigationArray => {
                investigationArray.forEach(item => {
                    if (item.sample) {
                        samplesSet.add(item.sample);
                    }
                });
            });
        });

        const capitalizeWords = (str) => {
            return str
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        };

        return Array.from(samplesSet)
            .map(s => capitalizeWords(s))
            .join(', ');
    };

    const getPageMargin = (margins) => {
        if(margins) {
            return [ margins.left, margins.top, margins.right, margins.bottom ]
        }

        return [ 60, 100, 60, 200 ];
    }

    const capitalize = (text) => {
        if(!text || typeof text !== 'string') {
            return '';
        }

        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    const shouldInclude = (customizationFields, key) => Object.keys(customizationFields).length === 0 || customizationFields[key];

    return {
        exportToPDF
    }
}

export default pdfPrinter;